import { useContext, useEffect, useState } from "react";
import { Page } from "pages/Page";
import { Promo } from "components/Promo";
import { RouteList } from "Routes";
import { useScrollToTop } from "hooks/useScrollToTop";
import { FrankyAnimation, getRandomFranky } from "types/FrankyAnimation";
import { HallOfFamePreview } from "components/content-pages/HallOfFamePreview";
import { IconText } from "components/IconText";
import { MainActions } from "components/MainActions";
import { Link, useHistory } from "react-router-dom";
import { GlobalContext } from "contexts/GlobalContext";
import { TestimonialsDisplay } from "components/TestimonialsDisplay";
import { GetHomeResult } from "results/frankenstories/GetHomeResult";
import clsx from "clsx";
import Benefit1 from "assets/img/home/benefit-1.jpg";
import Benefit2 from "assets/img/home/benefit-2.jpg";
import Benefit3 from "assets/img/home/benefit-3.jpg";
import Benefit4 from "assets/img/home/benefit-4.jpg";
import Benefit5 from "assets/img/home/benefit-5.jpg";
import Benefit6 from "assets/img/home/benefit-6.jpg";
import { HowToPlayCarousel } from "components/HowToPlayCarousel";
import { Video } from "components/videos/Video";
import AwardTechLearningPrimary from "assets/img/home/award-tech-learning-primary.png";
import AwardTechLearningSecondary from "assets/img/home/award-tech-learning-secondary.png";
import AwardTheJournal from "assets/img/home/award-the-journal.png";
import AwardMit from "assets/img/home/award-mit.png";

type ContentBlockProps = {
    img: string;
    heading: string;
    text: string;
};

const ContentBlock = (props: ContentBlockProps) => {
    const { img, heading, text } = props;

    return (
        <div className="g-col-12 g-col-lg-4">
            <div className="vstack gap-3">
                <div style={{ maxWidth: 400 }} className="mx-auto">
                    <img alt="" className="img-fluid" src={img} />
                </div>

                <h3 className="h5 text-secondary">{heading}</h3>

                <div>{text}</div>
            </div>
        </div>
    );
};

const ContentRow = (props: { items: Array<ContentBlockProps>; heading: string }) => {
    const { items, heading } = props;

    return (
        <section className="bg-white text-dark py-5 visually-hidden">
            <div className="container">
                <div className="vstack gap-4 gap-lg-5 text-center">
                    <h2 className="text-secondary">{heading}</h2>

                    <div className="grid gap-4 gap-lg-5">
                        {items.map(item => (
                            <ContentBlock key={item.heading} heading={item.heading} text={item.text} img={item.img} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const HomePage = () => {
    useScrollToTop();

    const history = useHistory();

    const [franky, setFranky] = useState<FrankyAnimation>();

    // set franky animation randomly
    useEffect(() => {
        const animation = getRandomFranky();
        setFranky(animation);
    }, []);

    const { contentService, catchServerError, isAuthenticated } = useContext(GlobalContext);
    const { getHome } = contentService;

    const [data, setData] = useState<GetHomeResult>();

    // redirect to games page
    useEffect(() => {
        if (isAuthenticated) {
            history.push(RouteList.GamesHome);
        }
    }, [history, isAuthenticated]);

    useEffect(() => {
        let mounted = true;

        if (!data) {
            getHome()
                .then(x => {
                    if (mounted) {
                        setData(x);
                    }
                })
                .catch(catchServerError);
        }

        return () => {
            mounted = false;
        };
    }, [catchServerError, data, getHome]);

    return (
        <Page
            title=""
            useContainer={false}
            description="Make writing exciting. Part collaborative, part competitive, completely motivating: your students won't want to stop writing."
            authType="Public"
            canonical={RouteList.Home}
            subNav={
                <div className="bg-success text-white auto-white-link">
                    <nav className="navbar navbar-home">
                        <div className="container">
                            <div className="py-2 py-lg-3 text-white text-center text-lg-start w-100">
                                <div
                                    className={clsx("animate", data ? "opacity-100" : "opacity-0")}
                                    dangerouslySetInnerHTML={{ __html: data?.promo ?? " " }}
                                ></div>
                            </div>
                        </div>
                    </nav>
                </div>
            }
        >
            <section className="container-mid">
                <div className="grid gap-lg-5 pb-5 flex-grow-1">
                    <div className="g-col-12 g-col-lg-5">
                        <div className="vstack gap-3">
                            <Promo type={franky} />
                        </div>
                    </div>

                    <div className="g-col-12 g-col-lg-7 vstack gap-4 justify-content-center text-center text-lg-start">
                        <h1 className="fs-3">
                            A creative writing game
                            <br />
                            for the whole class
                        </h1>

                        <MainActions />
                    </div>
                </div>
            </section>

            <section className="bg-white text-dark">
                <div className="container">
                    <div className="py-4 py-lg-5 px-lg-5">
                        <div className="vstack gap-4">
                            <Video id="6Mh59omeULM" />

                            <ul className="mb-0 mx-auto text-secondary h4 list-inline fw-bold text-center text-lg-start list-inline-piped">
                                <li className="list-inline-item">Grades 4-12+</li>
                                <li className="list-inline-item">30+ minutes</li>
                                <li className="list-inline-item">Any genre</li>
                                <li className="list-inline-item">Any language</li>
                            </ul>

                            <div className="text-center">
                                <Link to={RouteList.Videos} className="btn btn-dark icon-link">
                                    <IconText icon="fas fa-video" text="View more videos" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-secondary py-4 py-lg-5">
                <div className="container">
                    <div className="vstack gap-4 gap-lg-5">
                        <TestimonialsDisplay testimonials={data?.testimonials} />
                    </div>
                </div>
            </section>

            <section className="bg-gray py-4 py-lg-5">
                <div className="container">
                    <div className="d-flex flex-column flex-lg-row gap-4 justify-content-center">
                        <h2 className="h2 text-center text-secondary">
                            Ready to turn your own students <br className="d-none d-lg-block" />
                            into enthusiastic writers?
                        </h2>

                        <div className="align-self-center">
                            <Link className="btn btn-primary icon-link" to={RouteList.Signup}>
                                <IconText icon="fas fa-rocket" text="Register for a teacher account" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <ContentRow
                heading="Develop creative & confident writers"
                items={[
                    {
                        img: Benefit1,
                        heading: "Make writing exciting",
                        text: "Part collaborative, part competitive, completely motivating; your students won't want to stop writing.",
                    },
                    {
                        img: Benefit3,
                        heading: "A positive feedback loop",
                        text: "Hearts and votes from peers build confidence and help students see what works in real time.",
                    },
                    {
                        img: Benefit2,
                        heading: "Build writing stamina",
                        text: "Writing in repeated short, sharp bursts builds skills and stamina, one 30-minute game at a time.",
                    },
                ]}
            />

            <section className="bg-white py-5">
                <div className="container-mid">
                    <HowToPlayCarousel />
                </div>
            </section>

            <ContentRow
                heading="Get more with Frankenstories Pro"
                items={[
                    {
                        img: Benefit4,
                        heading: "Target skills that matter",
                        text: "Create your own prompts and round-by-round instructions (or use presets from our Prompt Library!)",
                    },
                    {
                        img: Benefit5,
                        heading: "Play with any size group",
                        text: "Split the class into small teams or even assign games as solo writing sprints.",
                    },
                    {
                        img: Benefit6,
                        heading: "Share prompts you love",
                        text: "Game templates let you save prompts for later use, or to share with other teachers.",
                    },
                ]}
            />

            <section className="py-4 py-lg-5">
                <div className="container">
                    <HallOfFamePreview title="See what other classes are writing!" />
                </div>
            </section>

            <section className="py-4 py-lg-5 bg-white">
                <div className="container">
                    <div className="vstack gap-4 text-center">
                        <h2 className="text-secondary">Awards &amp; recognition</h2>

                        <div className="grid gap-4 gap-lg-5">
                            <div className="g-col-12 g-col-lg-4 align-self-center">
                                <img
                                    alt="Winner The Journal - New Product of the Year"
                                    src={AwardTheJournal}
                                    className="img-fluid"
                                    style={{ maxWidth: 150 }}
                                />
                            </div>

                            <div className="g-col-12 g-col-lg-4 align-self-center">
                                <img
                                    alt="Winner Primary Education - Tech & Learning Awards of Excellence - Best of 2023"
                                    src={AwardTechLearningPrimary}
                                    className="img-fluid"
                                    style={{ maxWidth: 150 }}
                                />
                            </div>

                            <div className="g-col-12 g-col-lg-4 align-self-center">
                                <img
                                    alt="Winner Secondary Education - Tech & Learning Awards of Excellence - Best of 2023"
                                    src={AwardTechLearningSecondary}
                                    className="img-fluid"
                                    style={{ maxWidth: 150 }}
                                />
                            </div>

                            <div className="g-col-12">
                                <div className="vstack text-center position-relative">
                                    <img
                                        src={AwardMit}
                                        alt="MIT Solve"
                                        className="img-fluid align-self-center"
                                        style={{ maxWidth: 280 }}
                                    />

                                    <div className="vstack gap-3">
                                        <div className="h6 text-dark">We're a 2024 Challenge winner!</div>

                                        <div>
                                            <a
                                                target="_blank"
                                                className="btn btn-dark icon-link stretched-link"
                                                href="https://writelike.org/news/article/90/we-won-an-mit-solve-ai-grant-for-our-most-anti-ai-product"
                                            >
                                                <IconText icon="fas fa-arrow-right" text="Learn more here" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="pt-4 pt-lg-5 text-center text-lg-start">
                <div className="container">
                    <div className="d-flex flex-column flex-lg-row gap-4 gap-lg-5 justify-content-center">
                        <h2>What will your class write?</h2>

                        <div className="align-self-center">
                            <Link className="btn btn-primary icon-link" to={RouteList.Signup}>
                                <IconText icon="fas fa-rocket" text="Create a free teacher account" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </Page>
    );
};

export default HomePage;
